import React, { useState, useEffect } from 'react';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import Header from "../Master/header";
import Footer from "../Master/footer";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ResetPassword } from '../../Services/AuthService';
import { sweetStatusAlert } from "../../Helper/Dairy Helper/SweetAlertSetup.js";

const REQUIREMENTS = [
    { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
    { label: 'Include a number', test: (pw) => /\d/.test(pw) },
    { label: 'include a letter (a-z, A-Z)', test: (pw) => /[a-zA-Z]/.test(pw) },
    { label: 'Include a special character (e.g. !@#$%)', test: (pw) => /[!@#$%^&*(),.?":{}|<>[\]/\\~`_+=;-]/.test(pw) },
];

function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('This reset link is invalid or incomplete. Please request a new one.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('This reset link is invalid or incomplete. Please request a new one.');
            return;
        }

        const unmet = REQUIREMENTS.filter((req) => !req.test(newPassword));
        if (unmet.length > 0) {
            setError('Please meet all password requirements.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        try {
            const response = await ResetPassword({
                Token: token,
                Password: newPassword,
                ConfirmPassword: confirmPassword,
            });
            await sweetStatusAlert({ status: "success", message: response?.Message || response?.message || 'Password reset successfully.' });
            navigate('/Auth');
        } catch (err) {
            await sweetStatusAlert({ status: "error", message: err?.Message || err?.message || err?.response?.data?.Message || 'Failed to reset password. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <Header />

            <div className="min-h-[80vh] flex items-center justify-center bg-neutral-50 px-4 py-8">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8">
                    <Link to="/Auth"
                        className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium hover:text-orange-600"
                    >
                        <ArrowLeft size={16} />
                        Back to Login
                    </Link>

                    <div className="flex flex-col items-center mt-6 mb-8">
                        <h2 className="text-xl font-bold text-gray-900">Set a New Password</h2>
                        <p className="text-sm text-gray-500 text-center mt-2 max-w-xs">
                            Create a new password to regain access to your account and continue with CleanSlate.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-800 mb-1.5">
                                New Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-10 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>

                            <ul className="mt-3 space-y-1.5">
                                {REQUIREMENTS.map((req) => {
                                    const met = req.test(newPassword);
                                    return (
                                        <li
                                            key={req.label}
                                            className={`flex items-center gap-2 text-xs ${met ? 'text-green-600' : 'text-gray-400'
                                                }`}
                                        >
                                            <CheckCircle2
                                                size={14}
                                                className={met ? 'text-green-500' : 'text-gray-300'}
                                            />
                                            {req.label}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-800 mb-1.5">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-10 pr-10 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {error && <p className="text-xs text-red-500">{error}</p>}

                        <button
                            type="submit"
                            disabled={submitting || !token}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Resetting...' : 'Reset Password'}
                        </button>
                    </form>
                </div>
            </div>

            <Footer />
        </div>
    );
}

export default ResetPasswordPage;